import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { ApiError } from '@/types/api';

export interface ApiMeta {
  message?: string;
}

type RtkMutationResult = {
  data?: unknown;
  error?: FetchBaseQueryError | SerializedError;
  meta?: unknown;
};

export function getRtkMetaMessage(result: { meta?: unknown }): string | null {
  if (
    result.meta &&
    typeof result.meta === 'object' &&
    'message' in result.meta &&
    typeof (result.meta as ApiMeta).message === 'string'
  ) {
    return (result.meta as ApiMeta).message ?? null;
  }
  return null;
}

function extractMessageFromBody(body: unknown): string | null {
  if (typeof body === 'string') {
    return body;
  }

  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as ApiError).message;
    if (Array.isArray(message) && message.length > 0) {
      return String(message[0]);
    }
    if (typeof message === 'string') {
      return message;
    }
  }

  if (body && typeof body === 'object' && 'errors' in body) {
    const errors = (body as ApiError).errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return String(errors[0]);
    }
  }

  return null;
}

export function getRtkErrorMessage(error: unknown): string | null {
  if (!error) return null;
  if (typeof error !== 'object') return 'Something went wrong.';

  if ('data' in error) {
    const message = extractMessageFromBody(error.data);
    if (message) return message;
  }

  if ('message' in error && error.message) {
    return String(error.message);
  }

  return 'Something went wrong.';
}

export async function resolveMutationResult<T>(
  mutationPromise: Promise<RtkMutationResult>,
): Promise<
  { ok: true; data: T; message: string | null } | { ok: false; message: string }
> {
  const result = await mutationPromise;

  if (result.error) {
    return {
      ok: false,
      message: getRtkErrorMessage(result.error) ?? 'Something went wrong.',
    };
  }

  return {
    ok: true,
    data: result.data as T,
    message: getRtkMetaMessage(result),
  };
}
